import React, { useState, useEffect } from 'react';
import { SamplePrompt, SamplePromptCreateRequest, SamplePromptUpdateRequest, SamplePromptType } from '../../services/api/adminSamplePromptService';
import {
  TaskyButton,
  TaskyCheckbox,
  TaskyDialog,
  TaskyFormGroup,
  TaskyInput,
  TaskySelect,
  TaskyTextarea,
} from '../tasky';

interface SamplePromptFormModalProps {
  isOpen: boolean;
  prompt?: SamplePrompt | null;
  defaultType?: SamplePromptType;
  onSubmit: (data: SamplePromptCreateRequest | SamplePromptUpdateRequest) => void;
  onClose: () => void;
  isLoading?: boolean;
}

const SamplePromptFormModal: React.FC<SamplePromptFormModalProps> = ({
  isOpen,
  prompt,
  defaultType,
  onSubmit,
  onClose,
  isLoading = false
}) => {
  const [name, setName] = useState('');
  const [promptText, setPromptText] = useState('');
  const [type, setType] = useState<SamplePromptType>('material');
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (prompt) {
      setName(prompt.name);
      setPromptText(prompt.prompt_text);
      setType(prompt.type || 'material');
      setSortOrder(prompt.sort_order);
      setIsActive(prompt.is_active);
    } else {
      setName('');
      setPromptText('');
      setType(defaultType || 'material');
      setSortOrder(0);
      setIsActive(true);
    }
  }, [prompt, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      prompt_text: promptText,
      type,
      sort_order: sortOrder,
      is_active: isActive,
    });
  };

  return (
    <TaskyDialog
      open={isOpen}
      onClose={onClose}
      title={prompt ? 'サンプルプロンプト編集' : 'サンプルプロンプト新規作成'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <TaskyFormGroup label="ボタン名" htmlFor="sp-name">
          <TaskyInput
            id="sp-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="例: バリエーション違い"
            required
          />
        </TaskyFormGroup>

        <TaskyFormGroup label="プロンプトテキスト" htmlFor="sp-prompt-text">
          <TaskyTextarea
            id="sp-prompt-text"
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            placeholder="フリーテキスト入力欄に貼り付けるプロンプトテキスト"
            required
            rows={6}
          />
        </TaskyFormGroup>

        <TaskyFormGroup label="タイプ" htmlFor="sp-type">
          <TaskySelect
            id="sp-type"
            value={type}
            onChange={(e) => setType(e.target.value as SamplePromptType)}
          >
            <option value="material">素材生成</option>
            <option value="banner">広告生成</option>
          </TaskySelect>
        </TaskyFormGroup>

        <div className="flex gap-4">
          <TaskyFormGroup label="表示順" htmlFor="sp-sort-order" className="flex-1">
            <TaskyInput
              id="sp-sort-order"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              min={0}
            />
          </TaskyFormGroup>

          <TaskyFormGroup label="ステータス" className="flex-1">
            <div className="h-10 flex items-center">
              <TaskyCheckbox
                label="有効"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            </div>
          </TaskyFormGroup>
        </div>

        <div className="flex justify-end gap-3 border-t border-warm-border pt-4">
          <TaskyButton
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={isLoading}
          >
            キャンセル
          </TaskyButton>
          <TaskyButton
            type="submit"
            size="sm"
            disabled={isLoading || !name.trim() || !promptText.trim()}
            isLoading={isLoading}
          >
            {isLoading ? '保存中...' : (prompt ? '更新' : '作成')}
          </TaskyButton>
        </div>
      </form>
    </TaskyDialog>
  );
};

export default SamplePromptFormModal;
